import { Button } from '@authentic/loyalty-pwa/components';
import { TextField } from '@authentic/loyalty-pwa/components/Form';
import { ErrorIcon } from '@authentic/loyalty-pwa/icons';
import { Typography, objectIsEmpty } from '@authentic/loyalty-pwa/util';
import { breakpoints, colors } from '@authentic/loyalty-pwa/util/variables';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { SocialButtons } from '../components/SocialButtons';
import { Location } from '../components/Location';
import { link } from '@authentic/loyalty-pwa/util';

const FormStyled = styled.form`
  background: #ffffff;
  text-align: left;
`;

const FormSeparator = styled.div`
  border: 1px solid ${colors.gray1};

  &.first {
    margin-bottom: 35px;

    @media screen and (min-width: ${breakpoints.sm}) {
      display: none;
    }
  }
  &.second {
    margin: 40px 0;
  }
`;

const PasswordHint = styled.div`
  margin-bottom: 30px;
`;

const ErrorMessage = styled.div`
  color: ${colors.red};
  margin-bottom: 16px;

  .ico {
    display: inline-block;
    margin-right: 4px;
    vertical-align: middle;
  }
`;

const FormControlStyled = styled.div`
  margin-bottom: 12px;
`;

const AlreadyDiv = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const SocialButtonWrapper = styled.div`
  @media screen and (min-width: ${breakpoints.sm}) {
    display: flex;
    justify-content: center;
  }
`;

const passwordStrength = (value: string) => {
  const IS_NUM = new RegExp('.*\\d.*');
  const IS_LOWERCASE = new RegExp('.*[a-z].*');
  const IS_UPPERCASE = new RegExp('.*[A-Z].*');
  const IS_SPECIAL = new RegExp(".*[*.!@#$%^&(){}[]:;'<>,.?/~`_+-=|\\].*");

  return value.length < 8
    ? 0
    : [IS_NUM, IS_LOWERCASE, IS_UPPERCASE, IS_SPECIAL].filter(
        (validation) => !!validation.exec(value),
      ).length;
};

export const SignUpOne: FunctionalComponent<{
  callbackSubmit: (data: any) => void;
  formValues: any;
  customError: any;
}> = ({ callbackSubmit, formValues, customError }) => {
  const [defaultFormValues, setDefaultFormValues] = useState(formValues);

  const {
    setError,
    handleSubmit,
    getValues,
    control,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      firstName: defaultFormValues?.firstName,
      lastName: defaultFormValues?.lastName,
      mobile: defaultFormValues?.mobile,
      email: defaultFormValues?.email,
      birthdate: defaultFormValues?.birthdate,
      password: defaultFormValues?.password,
      passwordConfirmation: defaultFormValues?.passwordConfirmation,
    },
  });

  useEffect(() => {
    Object.keys(customError).forEach(key => {
      setError(key as any, { message: customError[key] }, { shouldFocus: true });
    })
  }, [customError])


  const onSubmit = (data: any) => {
    callbackSubmit(data);
  };

  return (
    <Fragment>
      <FormStyled onSubmit={handleSubmit(onSubmit)}>
        {customError.generic && (
          <ErrorMessage>
            <Typography variant="body1" weight="bold">
              <div className="ico">
                <ErrorIcon />
              </div>
              {customError.generic}
            </Typography>
          </ErrorMessage>
        )}
        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                error={errors?.firstName}
                label="First Name"
                required
                {...field}
                id="firstName"
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Please enter your First Name',
              },
            }}
            control={control}
            name="firstName"
            defaultValue=""
          />
        </FormControlStyled>

        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                error={errors?.lastName}
                label="Last Name"
                {...field}
                required
                id="lastName"
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Please enter your Last Name',
              },
            }}
            control={control}
            name="lastName"
            defaultValue=""
          />
        </FormControlStyled>
        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField error={errors?.email} label="Email" {...field} required id="email" />
            )}
            rules={{
              required: {
                value: true,
                message: 'Please enter your Email',
              },
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Entered value does not match email format',
              },
            }}
            control={control}
            name="email"
            defaultValue=""
          />
        </FormControlStyled>
        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                error={errors?.mobile}
                mask="(999) 999-9999"
                maskPlaceholder="(   )    -    "
                label="Mobile"
                type="tel"
                required
                {...field}
                id="mobile"
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Please enter your mobile',
              },
            }}
            control={control}
            name="mobile"
            defaultValue=""
          />
        </FormControlStyled>
        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                mask="99/99/9999"
                maskPlaceholder="mm/dd/yyyy"
                label="Birthday"
                {...field}
                error={errors?.birthdate}
                id="birthdate"
                warningInfo="We'll send you a reward on your birthday!"
              />
            )}
            control={control}
            name="birthdate"
            rules={{
              pattern: {
                value: /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/,
                message: 'Enter a valid date (mm/dd/yyyy)',
              },
            }}
            defaultValue=""
          />
        </FormControlStyled>
        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                error={errors?.password}
                label="Password"
                variant="password"
                type="password"
                {...field}
                required
                id="password"
              />
            )}
            rules={{
              validate: (value) =>
                passwordStrength(value) >= 3 ? true : 'Please enter valid password',
              required: {
                value: true,
                message: 'Please enter your password',
              },
              minLength: {
                value: 8,
                message: 'Password must have at least 8 characters',
              },
              maxLength: {
                value: 16,
                message: 'Password must not be longer than 16 characters',
              },
            }}
            control={control}
            name="password"
            defaultValue=""
          />
        </FormControlStyled>

        <FormControlStyled>
          <Controller
            render={({ field }: any): any => (
              <TextField
                error={errors?.passwordConfirmation}
                label="Confirm Password"
                variant="password"
                type="password"
                {...field}
                required
                id="passwordConfirmation"
              />
            )}
            rules={{
              required: {
                value: true,
                message: 'Please enter your password confirmation',
              },
              validate: (value) => value === getValues('password') || 'Passwords do not match',
            }}
            control={control}
            name="passwordConfirmation"
            defaultValue=""
          />
        </FormControlStyled>

        <PasswordHint>
          <Typography variant="body3">
            Password must contain 8-16 characters and all of the following: 1 upper, 1 lower, & 1
            number.
          </Typography>
        </PasswordHint>

        {!objectIsEmpty(errors) && (
          <ErrorMessage>
            <Typography variant="body1" weight="bold">
              <div className="ico">
                <ErrorIcon />
              </div>
              Review highlighted errors to continue
            </Typography>
          </ErrorMessage>
        )}

        <Button ariaLabel="NEXT" type="submit" fullWidth>
          NEXT
        </Button>

        <AlreadyDiv>
          <Typography variant="body1" weight="normal">
            Already have an account?{' '}
            <b>
              <a href={link('/sign-in')}> Sign In</a>
            </b>
          </Typography>
        </AlreadyDiv>
      </FormStyled>
      <FormSeparator className="second" />
      <Typography variant="body1">Or sign up with:</Typography>
      <SocialButtons />
    </Fragment>
  );
};
